// src/layers/glo/parseBlockSection.js
//
// Recognise a GLO survey block + section in something a person typed.
//
// ── WHY THIS IS NOT A TOKEN SPLIT ──────────────────────────────────────────
//
// The obvious parser takes the word after "block" and the word after
// "section". It is wrong on this corpus, because block and section values are
// not tokens and are usually not numbers.
//
// Measured on aiprime.glo_ols_polygons 2026-09-08:
//   only 378 of 1,419 distinct BLOCK values are purely numeric
//   only 3,422 of 4,826 distinct SECTION values are purely numeric
//
// Real values include "36 T4S", "E 1/4", "61 T1", "100 1/2", "1004A". A block
// value CONTAINS A SPACE, so "Block 36 T4S Section 9" has block "36 T4S" and
// not "36". This parser therefore splits on the section KEYWORD and treats
// everything on each side as a whole value, rather than reading one word.
//
// ── WHY "S" DOES NOT MATCH INSIDE "T4S" ────────────────────────────────────
//
// The section keyword must start at a word boundary AND be followed by a
// separator or the value. In "36 T4S" the S is preceded by "4", and \b does
// not fire between two word characters, so the block value survives intact.
// That is load-bearing rather than lucky: "36 T4S" is a real block.

// Order matters: the longest keyword first, so "section" is not consumed as
// "sec" leaving "tion" as the value.
const SECTION_KW = /\b(?:sections?|sects?|secs?|s)\s*[-.:#]?\s*/i;
const BLOCK_KW = /^\s*(?:blocks?|blks?|b)\s*[-.:#]?\s*/i;

/** Collapse runs of whitespace and trim. Values are compared normalised. */
function squash(s) {
  return String(s ?? "").replace(/\s+/g, " ").trim();
}

/**
 * @param {string} input
 * @returns {{block: string, section: string} | null}
 *
 * Returns null when the string does not look like a block+section at all,
 * which is the signal for the caller to fall through to geocoding. BOTH parts
 * are required: block alone matches an envelope larger than Texas for 26% of
 * block values, so it is not a fly-to target.
 */
export function parseBlockSection(input) {
  const raw = squash(input);
  if (!raw) return null;

  // Find the section keyword ANYWHERE after the first character — a leading
  // "S" would mean the string starts with the section, which the swap below
  // handles, but a section keyword at index 0 with no block before it is not
  // a block+section query.
  const m = SECTION_KW.exec(raw);
  if (!m || m.index === 0) return null;

  const beforeKw = raw.slice(0, m.index);
  const afterKw = raw.slice(m.index + m[0].length);

  const section = squash(afterKw);
  if (!section) return null;

  // The block half: strip a leading block keyword if there is one. A bare
  // "24 S-9" is accepted -- the keyword is optional on the block, because the
  // section keyword alone already establishes the shape.
  const blockPart = squash(beforeKw).replace(/[,;]+$/, "");
  const block = squash(blockPart.replace(BLOCK_KW, ""));
  if (!block) return null;

  // A block that is still nothing but a keyword remnant is not a value.
  if (/^[-.:#]+$/.test(block)) return null;

  return { block, section };
}
